import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-apollo';

import useConsentManager from '../../../hooks/useConsentManager';
import { CONSENT_COOKIE_CATEGORIES } from '../../../constants/gdpr';
import Button from '../Button';
import CustomControl from '../CustomControl';
import { ALL_PHOTOS_MODE } from '../../../constants/album-folder';
import useAccount from '../hooks/useAccount';
import getConfig from '../../../util/config';
import { accountUpdate as accountUpdateMutation } from '../../../graphql/mutations/account';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    is_web_gallery: PropTypes.bool,
  }),
  onAccept: PropTypes.func,
};

const GDPRFaceRecognitionConsent = ({ onClose, albumFolder, onAccept }) => {
  const history = useHistory();
  const { account } = useAccount();
  const { hasConsent, saveConsent } = useConsentManager();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { marcomUrl } = getConfig();
  const [accountUpdate] = useMutation(accountUpdateMutation);

  const hasViewableWebGallery =
    !!albumFolder?.is_web_gallery &&
    albumFolder?.all_photos_mode !== ALL_PHOTOS_MODE.PRIVATE;

  useEffect(() => {
    // If user already has consent, close the modal immediately
    if (account?.privacy_settings) {
      onClose?.();
    }
  }, [account]);

  const onAcceptConsent = async () => {
    if (!isChecked) return;

    setIsProcessing(true);
    try {
      // Update account with privacy settings
      await accountUpdate({
        variables: {
          input: {
            id: account.id,
            privacy_settings: {
              face_recognition_consent: isChecked,
            },
          },
        },
      });

      saveConsent({
        [CONSENT_COOKIE_CATEGORIES.ESSENTIAL]: true,
        [CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL]: hasConsent(
          CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL,
        ),
      });
      onAccept?.();
      onClose?.();
    } catch (error) {
      setIsProcessing(false);
    }
  };

  const onViewWebGallery = () => {
    setIsProcessing(true);
    history.push(`/galleries/view/folder/${albumFolder.id}`);
    onClose?.();
  };

  return (
    <Fragment>
      <div className="text-center mb-3">
        <h2 className="mb-3">Facial Recognition Consent</h2>
      </div>

      <div className="mb-4 mx-1 mx-md-2 text-center">
        <CustomControl
          size="md"
          shape="squared"
          type="checkbox"
          checked={isChecked}
          onChange={e => setIsChecked(e.target.checked)}
          disabled={isProcessing}
          label="I agree to Waldo's Terms of Use and Privacy Policy, including consent to the use of facial recognition."
        />
      </div>

      <div className="text-center mb-3">
        <Button
          btnStyle="link"
          onClick={() => {
            window.open(`${marcomUrl}/terms`, '_blank', 'noopener,noreferrer');
          }}
          className="text-sm"
        >
          View Terms of Use &amp; Privacy Policy
        </Button>
      </div>

      <div className="text-center">
        <Button
          btnBlock
          btnStyle="primary"
          onClick={onAcceptConsent}
          disabled={isProcessing || !isChecked}
        >
          {isProcessing ? 'Processing...' : 'Continue'}
        </Button>
        {hasViewableWebGallery && (
          <Button
            btnBlock
            btnOutline
            onClick={onViewWebGallery}
            disabled={isProcessing}
            className="mt-1"
          >
            View web gallery without joining
          </Button>
        )}
      </div>
    </Fragment>
  );
};

GDPRFaceRecognitionConsent.propTypes = propTypes;

export { GDPRFaceRecognitionConsent as GDPRFaceRecognitionConsentModalComponent };

export default GDPRFaceRecognitionConsent;
